import { MongoAbility, MongoQuery, subject } from '@casl/ability'
import {
    SmartlyActionItems,
    SmartlyActionTypes,
} from '../Techedge-digitalportal-shared'

export class UserAbilityHelper {
    ability: MongoAbility<[SmartlyActionTypes, SmartlyActionItems], MongoQuery>
    constructor(
        ability: MongoAbility<
            [SmartlyActionTypes, SmartlyActionItems],
            MongoQuery
        >
    ) {
        this.ability = ability
    }

    /**********************************CREATE PERMISSIONS**********************************/

    public canCreate = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.CREATE,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.CREATE,
                item as SmartlyActionItems
            )
        }
    }

    public canCreateTask = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.TASK, model)
    }

    public canCreateNews = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.NEWS, model)
    }

    public canCreateVisit = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.VISIT, model)
    }
    public hasMysteryVisitDisabled = (): boolean => {
        return this.ability.can(
            SmartlyActionTypes.MISTERY_VISIT_DISABLED,
            SmartlyActionItems.VISIT
        )
    }

    public canCreateReleased = (
        item: SmartlyActionItems,
        model?: any
    ): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.CREATE_RELEASED,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.CREATE_RELEASED,
                item as SmartlyActionItems
            )
        }
    }

    public canCreateReleasedTask = (model?: any): boolean => {
        return this.canCreateReleased(SmartlyActionItems.TASK, model)
    }

    public canCreateReleasedNews = (model?: any): boolean => {
        return this.canCreateReleased(SmartlyActionItems.NEWS, model)
    }
    public canCreateReleasedVisit = (model?: any): boolean => {
        return this.canCreateReleased(SmartlyActionItems.VISIT, model)
    }

    public canCreatePhoto = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.PHOTO, model)
    }

    public canCreateTopic = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.TOPIC, model)
    }
    public canCreateThread = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.THREAD, model)
    }
    public canCreateMessage = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.MESSAGE, model)
    }

    public canCreateActivity = (model?: any): boolean => {
        return this.canCreateTask(model) || this.canCreateNews(model)
    }

    public canCreateQuickLink = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.QUICK_LINK, model)
    }

    /**********************************CREATE AS DRAFT PERMISSIONS**********************************/

    public canCreateAsDraft = (
        item: SmartlyActionItems,
        model?: any
    ): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.CREATE_AS_DRAFT,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.CREATE_AS_DRAFT,
                item as SmartlyActionItems
            )
        }
    }

    public canCreateAsDraftTask = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.TASK, model)
    }

    public canCreateAsDraftNews = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.NEWS, model)
    }

    /**********************************EDIT PERMISSIONS**********************************/

    public canEdit = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.EDIT,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.EDIT,
                item as SmartlyActionItems
            )
        }
    }

    public canEditTask = (model?: any): boolean => {
        return this.canEdit(SmartlyActionItems.TASK, model)
    }

    public canEditNews = (model?: any): boolean => {
        return this.canEdit(SmartlyActionItems.NEWS, model)
    }

    public canEditQuickLink = (model?: any): boolean => {
        return this.canEdit(SmartlyActionItems.QUICK_LINK, model)
    }
    public canEditThread = (model?: any): boolean => {
        return this.canEdit(SmartlyActionItems.THREAD, model)
    }
    public canEditTopic = (model?: any): boolean => {
        return this.canEdit(SmartlyActionItems.TOPIC, model)
    }

    /**********************************SAVE DRAFT PERMISSIONS**********************************/

    public canSaveDraft = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.SAVEDRAFT,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.SAVEDRAFT,
                item as SmartlyActionItems
            )
        }
    }

    public canSaveDraftTask = (model?: any): boolean => {
        return this.canSaveDraft(SmartlyActionItems.TASK, model)
    }

    public canSaveDraftNews = (model?: any): boolean => {
        return this.canSaveDraft(SmartlyActionItems.NEWS, model)
    }

    /**********************************SAVE AS TO BE RELEASED PERMISSIONS**********************************/

    public canSaveAsToBeReleased = (
        item: SmartlyActionItems,
        model?: any
    ): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.SEND_TO_RELEASE,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.SEND_TO_RELEASE,
                item as SmartlyActionItems
            )
        }
    }

    public canSaveAsToBeReleasedTask = (model?: any): boolean => {
        return this.canSaveAsToBeReleased(SmartlyActionItems.TASK, model)
    }

    public canSaveAsToBeReleasedNews = (model?: any): boolean => {
        return this.canSaveAsToBeReleased(SmartlyActionItems.NEWS, model)
    }

    /**********************************SAVE DRAFT PERMISSIONS**********************************/

    public canSendInApproval = (
        item: SmartlyActionItems,
        model?: any
    ): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.SENDINAPPROVAL,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.SENDINAPPROVAL,
                item as SmartlyActionItems
            )
        }
    }

    public canSendInApprovalPhotographicTask = (
        item: SmartlyActionItems,
        model?: any
    ): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.SENDINAPPROVAL_PHOTOGRAPHIC_TASK,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.SENDINAPPROVAL_PHOTOGRAPHIC_TASK,
                item as SmartlyActionItems
            )
        }
    }

    public canSendInApprovalTask = (model?: any): boolean => {
        return this.canSendInApproval(SmartlyActionItems.TASK, model)
    }

    public canSendInApprovalNews = (model?: any): boolean => {
        return this.canSendInApproval(SmartlyActionItems.NEWS, model)
    }

    public canAcceptApproval = (
        item: SmartlyActionItems,
        model?: any
    ): boolean => {
        try {
            if (model) {
                return this.ability.can(
                    SmartlyActionTypes.ACCEPT_APPROVAL,
                    subject(item as SmartlyActionItems, model)
                )
            } else {
                return this.ability.can(
                    SmartlyActionTypes.ACCEPT_APPROVAL,
                    item as SmartlyActionItems
                )
            }
        } catch (err) {
            return false
        }
    }

    /**********************************CANCEL PERMISSIONS**********************************/

    public canCancel = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.CANCEL,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.CANCEL,
                item as SmartlyActionItems
            )
        }
    }

    public canCancelTask = (model?: any): boolean => {
        return this.canCancel(SmartlyActionItems.TASK, model)
    }

    public canCancelNews = (model?: any): boolean => {
        return this.canCancel(SmartlyActionItems.NEWS, model)
    }

    /**********************************DELETE PERMISSIONS**********************************/

    public canDelete = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.DELETE,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.DELETE,
                item as SmartlyActionItems
            )
        }
    }

    public canDeleteThread = (model?: any) => {
        return this.canDelete(SmartlyActionItems.THREAD, model)
    }
    public canDeleteTopic = (model?: any) => {
        return this.canDelete(SmartlyActionItems.TOPIC, model)
    }

    /**********************************MOVE PERMISSIONS**********************************/

    public canMove = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.MOVE,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.MOVE,
                item as SmartlyActionItems
            )
        }
    }

    /**********************************RELEASE PERMISSIONS**********************************/

    public canRelease = (item: SmartlyActionItems, model?: any): boolean => {
        // console.log('CAN RELEASE -----------------')
        // console.log('MODEL', model)
        // console.log(
        //     `PERMITTED: ${this.ability.can(
        //         SmartlyActionTypes.CLONE,
        //         subject(item as SmartlyActionItems, model)
        //     )}`
        // )
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.RELEASE,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.RELEASE,
                item as SmartlyActionItems
            )
        }
    }

    public canReleaseTask = (model?: any): boolean => {
        return this.canRelease(SmartlyActionItems.TASK, model)
    }

    public canReleaseNews = (model?: any): boolean => {
        return this.canRelease(SmartlyActionItems.NEWS, model)
    }

    /**********************************CLONE PERMISSIONS**********************************/

    public canClone = (item: SmartlyActionItems, model?: any): boolean => {
        // console.log('CAN CLONE -----------------')
        // console.log('MODEL', model)
        // console.log(
        //     `PERMITTED: ${this.ability.can(
        //         SmartlyActionTypes.CLONE,
        //         subject(item as SmartlyActionItems, model)
        //     )}`
        // )
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.CLONE,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.CLONE,
                item as SmartlyActionItems
            )
        }
    }

    public canCloneTask = (model?: any): boolean => {
        return this.canClone(SmartlyActionItems.TASK, model)
    }

    public canCloneNews = (model?: any): boolean => {
        return this.canClone(SmartlyActionItems.NEWS, model)
    }

    /**********************************CLOSE PERMISSIONS**********************************/

    public canClose = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.CLOSE,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.CLOSE,
                item as SmartlyActionItems
            )
        }
    }

    public canClosePhoto = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.CLOSE_PHOTO,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.CLOSE_PHOTO,
                item as SmartlyActionItems
            )
        }
    }

    public canCloseTask = (model?: any): boolean => {
        return this.canClose(SmartlyActionItems.TASK, model)
    }

    public canCloseNews = (model?: any): boolean => {
        return this.canClose(SmartlyActionItems.NEWS, model)
    }

    public canGoToGallery = (
        item: SmartlyActionItems,
        model?: any
    ): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.GO_TO_GALLERY,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.GO_TO_GALLERY,
                item as SmartlyActionItems
            )
        }
    }

    /**********************************REDO PERMISSIONS**********************************/

    public canRedo = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.REDO,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.REDO,
                item as SmartlyActionItems
            )
        }
    }

    public canRedoTask = (model?: any): boolean => {
        return this.canRedo(SmartlyActionItems.TASK, model)
    }

    public canRedoNews = (model?: any): boolean => {
        return this.canRedo(SmartlyActionItems.NEWS, model)
    }

    /**********************************DELEGATE PERMISSIONS**********************************/

    public canDelegate = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.DELEGATE,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.DELEGATE,
                item as SmartlyActionItems
            )
        }
    }

    public canDelegateTask = (model?: any): boolean => {
        return this.canDelegate(SmartlyActionItems.TASK, model)
    }

    public canDelegateNews = (model?: any): boolean => {
        return this.canDelegate(SmartlyActionItems.NEWS, model)
    }

    /**********************************REJECT PERMISSIONS**********************************/

    public canReject = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.REJECT,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.REJECT,
                item as SmartlyActionItems
            )
        }
    }

    public canRejectTask = (model?: any): boolean => {
        return this.canReject(SmartlyActionItems.TASK, model)
    }

    public canRejectNews = (model?: any): boolean => {
        return this.canReject(SmartlyActionItems.NEWS, model)
    }

    /**********************************EXECUTE PERMISSIONS**********************************/

    public canExecuteQuestionTask = (model?: any): boolean => {
        try {
            if (model) {
                return this.ability.can(
                    SmartlyActionTypes.EXECUTE_QUESTION,
                    subject(SmartlyActionItems.TASK, model)
                )
            } else {
                return this.ability.can(
                    SmartlyActionTypes.REJECT,
                    SmartlyActionItems.TASK
                )
            }
        } catch (err) {
            return false
        }
    }

    public canExecuteJobTask = (model?: any): boolean => {
        try {
            if (model) {
                return this.ability.can(
                    SmartlyActionTypes.EXECUTE_JOB,
                    subject(SmartlyActionItems.TASK, model)
                )
            } else {
                return this.ability.can(
                    SmartlyActionTypes.EXECUTE_JOB,
                    SmartlyActionItems.TASK
                )
            }
        } catch (err) {
            return false
        }
    }

    public canExecuteSurvey = (
        item: SmartlyActionItems,
        model?: any
    ): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.EXECUTE_SURVEY,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.EXECUTE_SURVEY,
                item as SmartlyActionItems
            )
        }
    }

    public canExecuteSurveyTask = (model?: any): boolean => {
        return this.canExecuteSurvey(SmartlyActionItems.TASK, model)
    }

    public canConfirmReadingNews = (
        item: SmartlyActionItems,
        model?: any
    ): boolean => {
        try {
            if (model) {
                return this.ability.can(
                    SmartlyActionTypes.CONFIRM_READING,
                    subject(item as SmartlyActionItems, model)
                )
            } else {
                return this.ability.can(
                    SmartlyActionTypes.CONFIRM_READING,
                    item as SmartlyActionItems
                )
            }
        } catch (err) {
            return false
        }
    }

    public canAcceptExecution = (
        item: SmartlyActionItems,
        model?: any
    ): boolean => {
        try {
            if (model) {
                return this.ability.can(
                    SmartlyActionTypes.ACCEPT_EXECUTION,
                    subject(item as SmartlyActionItems, model)
                )
            } else {
                return this.ability.can(
                    SmartlyActionTypes.ACCEPT_EXECUTION,
                    item as SmartlyActionItems
                )
            }
        } catch (err) {
            return false
        }
    }

    public canUploadPhotoTask = (model?: any): boolean => {
        try {
            if (model) {
                return this.ability.can(
                    SmartlyActionTypes.UPLOAD_PHOTO,
                    subject(SmartlyActionItems.TASK, model)
                )
            } else {
                return this.ability.can(
                    SmartlyActionTypes.UPLOAD_PHOTO,
                    SmartlyActionItems.TASK
                )
            }
        } catch (err) {
            return false
        }
    }

    /**********************************RECALL PERMISSIONS**********************************/

    public canRecall = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.RECALL,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.RECALL,
                item as SmartlyActionItems
            )
        }
    }

    public canRecallTask = (model?: any): boolean => {
        return this.canEdit(SmartlyActionItems.TASK, model)
    }

    public canRecallNews = (model?: any): boolean => {
        return this.canEdit(SmartlyActionItems.NEWS, model)
    }

    /**********************************RESOURCES**********************************/

    public canCreateResource = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.RESOURCE, model)
    }

    public canCreateDocument = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.DOCUMENT, model)
    }

    public canCreateForm = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.FORM, model)
    }

    public canEditResource = (model?: any): boolean => {
        return this.canEdit(SmartlyActionItems.RESOURCE, model)
    }

    public canEditDocument = (model?: any): boolean => {
        return this.canEdit(SmartlyActionItems.DOCUMENT, model)
    }

    public canEditForm = (model?: any): boolean => {
        return this.canEdit(SmartlyActionItems.FORM, model)
    }

    public canDeleteResource = (model?: any): boolean => {
        return this.canDelete(SmartlyActionItems.RESOURCE, model)
    }

    public canDeleteDocument = (model?: any): boolean => {
        return this.canDelete(SmartlyActionItems.DOCUMENT, model)
    }

    public canDeleteForm = (model?: any): boolean => {
        return this.canDelete(SmartlyActionItems.FORM, model)
    }

    public canMoveResource = (model?: any): boolean => {
        return this.canMove(SmartlyActionItems.RESOURCE, model)
    }

    public canMoveDocument = (model?: any): boolean => {
        return this.canMove(SmartlyActionItems.DOCUMENT, model)
    }

    public canMoveForm = (model?: any): boolean => {
        return this.canMove(SmartlyActionItems.FORM, model)
    }

    /**********************************PROFILE MANAGEMENT PERMISSIONS**********************************/

    public canMenu = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.PROFILE_MGMT,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.PROFILE_MGMT,
                item as SmartlyActionItems
            )
        }
    }

    public canEditAlias = (model?: any): boolean => {
        return this.ability.can(SmartlyActionTypes.EDIT_ALIAS, model)
    }

    public canEditProfiles = (model?: any): boolean => {
        return this.canMenu(SmartlyActionItems.MENU, model)
    }

    /**********************************VIEW PERMISSIONS**********************************/

    public canView = (item: SmartlyActionItems, model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.VIEW_REPORTS,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.VIEW_REPORTS,
                item as SmartlyActionItems
            )
        }
    }

    public canViewReports = (model?: any): boolean => {
        return this.canView(SmartlyActionItems.MENU, model)
    }

    public canViewProjectDetailsExtraInfo = (
        item: SmartlyActionItems,
        model?: any
    ): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.VIEW_INFO_EXTRA,
                subject(item as SmartlyActionItems, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.VIEW_INFO_EXTRA,
                item as SmartlyActionItems
            )
        }
    }

    /**********************************GALLERY PHOTO ACTION PERMISSIONS**********************************/

    public canPhotoApproval = (model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.PHOTO_APPROVAL,
                subject(SmartlyActionItems.TASK, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.PHOTO_APPROVAL,
                SmartlyActionItems.TASK
            )
        }
    }
    public canProjectPhotoApproval = (model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.PROJECT_PHOTO_APPROVAL,
                subject(SmartlyActionItems.TASK, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.PROJECT_PHOTO_APPROVAL,
                SmartlyActionItems.TASK
            )
        }
    }
    public canGalleryPhotoApproval = (model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.GALLERY_PHOTO_APPROVAL,
                subject(SmartlyActionItems.TASK, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.GALLERY_PHOTO_APPROVAL,
                SmartlyActionItems.TASK
            )
        }
    }

    public canPhotoReject = (model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.PHOTO_REJECT,
                subject(SmartlyActionItems.TASK, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.PHOTO_REJECT,
                SmartlyActionItems.TASK
            )
        }
    }
    public canProjectPhotoReject = (model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.PROJECT_PHOTO_REJECT,
                subject(SmartlyActionItems.TASK, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.PROJECT_PHOTO_REJECT,
                SmartlyActionItems.TASK
            )
        }
    }
    public canGalleryPhotoReject = (model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.GALLERY_PHOTO_REJECT,
                subject(SmartlyActionItems.TASK, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.GALLERY_PHOTO_REJECT,
                SmartlyActionItems.TASK
            )
        }
    }

    public canPhotoPublish = (model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.PHOTO_PUBLISH,
                subject(SmartlyActionItems.TASK, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.PHOTO_PUBLISH,
                SmartlyActionItems.TASK
            )
        }
    }
    public canProjectPhotoPublish = (model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.PROJECT_PHOTO_PUBLISH,
                subject(SmartlyActionItems.TASK, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.PROJECT_PHOTO_PUBLISH,
                SmartlyActionItems.TASK
            )
        }
    }
    public canGalleryPhotoPublish = (model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.GALLERY_PHOTO_PUBLISH,
                subject(SmartlyActionItems.TASK, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.GALLERY_PHOTO_PUBLISH,
                SmartlyActionItems.TASK
            )
        }
    }
    public canGalleryPhotoSendInApproval = (model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.GALLERY_PHOTO_SEND_IN_APPROVAL,
                subject(SmartlyActionItems.TASK, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.GALLERY_PHOTO_SEND_IN_APPROVAL,
                SmartlyActionItems.TASK
            )
        }
    }
    public canPhotoSendInApproval = (model?: any): boolean => {
        if (model) {
            return this.ability.can(
                SmartlyActionTypes.PHOTO_SEND_IN_APPROVAL,
                subject(SmartlyActionItems.TASK, model)
            )
        } else {
            return this.ability.can(
                SmartlyActionTypes.PHOTO_SEND_IN_APPROVAL,
                SmartlyActionItems.TASK
            )
        }
    }

    /**************************ALBUM*******************************/

    public canCreateAlbum = (model?: any): boolean => {
        return this.canCreate(SmartlyActionItems.ALBUM, model)
    }
    public canEditAlbum = (model?: any): boolean => {
        return this.canEdit(SmartlyActionItems.ALBUM, model)
    }
    public canDeleteAlbum = (model?: any): boolean => {
        return this.canDelete(SmartlyActionItems.ALBUM, model)
    }
}
